<script lang="ts">
    import Vue from "vue";
    import { Component } from 'vue-facing-decorator';
    import { EditorState } from '@common/Common';
    import { DiscreteScaleLevelReadModel } from '@Scales/Domain/readmodels';
    import { DiscreteScaleEditorViewModel } from "@Scales/Components/DiscreteScaleEditor";


    @Component({
        props: {
            viewModel: {
                type: Object,
                required: true
            }
        }
    })
    export default class DiscreteScaleEditor extends Vue
    {
        get vm(): DiscreteScaleEditorViewModel { return this.$props.viewModel as DiscreteScaleEditorViewModel; }

        get opened(): boolean { return this.vm?.opened ?? false; }
        get confirmButtonLabel(): string { return this.vm.state === EditorState.Creator ? 'Create' : 'Update'; }
        get modalTitle(): string { return this.vm.state === EditorState.Creator ? 'Create scale' : 'Edit scale'; }
        get canDelete(): boolean { return this.vm.state === EditorState.Editor; }
        get nameError(): string | null { return this.vm.nameError; }
        get descriptionError(): string | null { return this.vm.descriptionError; }
        get levelsError(): string | null { return this.vm.levelsError; }

        get levels() { return this.vm.levels; }

        get name(): string | null { return this.vm.name; }
        set name(val: string | null) { this.vm.name = val; }

        get description(): string | null { return this.vm.description; }
        set description(val: string | null) { this.vm.description = val; }


        removeScaleLevel(lvl: DiscreteScaleLevelReadModel)
        {
            this.vm.levels.remove(lvl);
        }

        addScaleLevel()
        {
            this.vm.levels.push(new DiscreteScaleLevelReadModel());
        }

        handleFormSubmit()
        {
            this.vm.clearValidationErrors();

            if (this.vm.state === EditorState.Creator)
            {
                this.$emit("create-scale", this.name, this.description, JSON.stringify(this.levels));
            }
            else if (this.vm.state === EditorState.Editor)
            {
                this.$emit("update-scale", this.vm.scaleId, this.name, this.description, JSON.stringify(this.levels));
            }
        }

        handleDeleteScale()
        {
            this.$emit("delete-scale", this.vm.scaleId);
        }

        close()
        {
            this.vm.close();
        }
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ modalTitle }}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <form id="discrete-scale-editor-form" @submit.prevent="handleFormSubmit">
                            <div class="form-group">
                                <label>Name:</label>
                                <input type="text" v-model.trim="name" class="form-control" required minlength="2" maxlength="100" />
                                <div class="error">{{nameError}}</div>
                            </div>

                            <div class="form-group">
                                <label>Description:</label>
                                <textarea v-model.trim="description" rows="3" class="form-control"></textarea>
                                <div class="error">{{descriptionError}}</div>
                            </div>

                            <div class="row" style="margin-bottom: 5px;">
                                <div class="col-sm-3">
                                    <label>Scale level <span class="text-danger">*</span></label>
                                </div>
                                <div class="col-sm-2">
                                    <label>Value <span class="text-danger">*</span></label>
                                </div>
                                <div class="col-sm-6">
                                    <label>Definition</label>
                                </div>
                            </div>

                            <div v-for="l in levels">
                                <div class="row" style="margin-bottom: 5px;">
                                    <div class="col-sm-3">
                                        <input type="text" v-model.trim="l.Name" class="form-control" placeholder="Level" required minlength="2" maxlength="100">
                                        <div class="error">{{l.NameError}}</div>
                                    </div>
                                    <div class="col-sm-2">
                                        <input type="number" v-model.number="l.Value" min="0" placeholder="Number..." class="form-control" required>
                                        <div class="error">{{l.ValueError}}</div>
                                    </div>
                                    <div class="col-sm-6">
                                        <textarea type="text" v-model.trim="l.Definition" rows="1" class="form-control" placeholder="Definition for level" />
                                    </div>
                                    <div class="col-sm-1">
                                        <a @click="removeScaleLevel(l)" class="pointer fa fa-times"></a>
                                    </div>
                                </div>
                            </div>
                            <div class="error">{{levelsError}}</div>

                            <p>
                                <a class="text-primary" @click="addScaleLevel">Add scale level</a>
                            </p>
                        </form>
                    </div>
                        
                    <div class="modal-footer justify-content-between">
                        <div class="dropdown dropup">
                            <button type="button" v-if="canDelete" class="btn btn-danger" data-toggle="dropdown">
                                Delete
                            </button>
                            <div class="dropdown-menu" style="padding: 10px;">
                                <a class="dropdown-item" @click="handleDeleteScale">Confirm</a>
                                <a class="dropdown-item">Cancel</a>
                            </div>
                        </div>
                        <div>
                            <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                            <button type="submit" form="discrete-scale-editor-form" class="btn btn-primary">{{ confirmButtonLabel }}</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>      


<style scoped>
</style>